/**
 * Client API Service
 * Handles client-related API calls
 */

import API_BASE_URL from './apiConfig';
import { getAuthHeaders, getUser } from './authApi';
import {
  buildClientsListCacheKey,
  getCachedClientsList,
  setCachedClientsList,
  clearClientsListCache,
} from '../utils/clientsListCache';

/**
 * Fetch list of clients with optional search
 * @param {string} search - Search query for client name (optional)
 * @param {number} limit - Maximum number of results (default: 50)
 * @param {Object} [options]
 * @param {boolean} [options.skipCache] - bypass localStorage
 * @param {string} [options.cacheUserId] - scope cache (defaults to logged-in employee_id)
 * @returns {Promise<{success: boolean, data: Array, error?: string}>}
 */
export const fetchClients = async (search = '', limit = 50, options = {}) => {
  const { skipCache = false, cacheUserId } = options;
  const uid = cacheUserId !== undefined ? cacheUserId : (getUser()?.employee_id || '');
  const cacheKey = buildClientsListCacheKey(search || '', limit, uid);

  if (!skipCache) {
    const cached = getCachedClientsList(cacheKey);
    if (cached) return cached;
  }

  try {
    const params = new URLSearchParams();

    if (search && search.trim()) {
      params.append('search', search.trim());
    }

    params.append('limit', limit.toString());

    const response = await fetch(`${API_BASE_URL}/client?${params.toString()}`, {
      method: 'GET',
      cache: 'no-store',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders(),
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: `HTTP error! status: ${response.status}` }));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    const data = await response.json();

    const result = {
      success: true,
      data: Array.isArray(data) ? data : [],
    };
    if (!skipCache && result.success) {
      setCachedClientsList(cacheKey, result);
    }
    return result;
  } catch (error) {
    return {
      success: false,
      error: error.message || 'Failed to fetch clients',
      data: [],
    };
  }
};

/**
 * Full client list for proposal / selection flows (uses existing GET /client endpoint).
 * @param {Object} [options] - passed to fetchClients ({ skipCache, cacheUserId })
 * @returns {Promise<Array>}
 */
export async function getAllClient(options = {}) {
  const result = await fetchClients('', 5000, options);
  if (!result.success) {
    throw new Error(result.error || 'Failed to fetch clients');
  }
  return result.data;
}

/**
 * Create a new client (POST /client).
 * @param {Object} payload — body from getAddClientPayload (see utils/apiControllers)
 * @returns {Promise<Object>} Created client document (or { data } wrapper if API returns one)
 */
export async function addClient(payload) {
  const response = await fetch(`${API_BASE_URL}/client`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders(),
    },
    body: JSON.stringify(payload),
  });

  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(data.message || data.error || `Request failed (${response.status})`);
  }
  clearClientsListCache();
  return data;
}

/** Named API surface for hooks and proposal flow */
export const API = {
  getAllClient,
  addClient,
};

export { clearClientsListCache };

